import { useEffect, useState, useCallback, useRef } from 'react'
import { flowHubService, FlowChange, FlowParticipant, FlowChangeData } from '@services/flowHub.service'
import { tokenStorage } from '@services/tokenStorage'
import { useAuthStore } from '@store/authStore'
import { useWorkflowStore, WorkflowNode, WorkflowEdge } from '@store/workflowStore'

export interface UserJoinedEvent {
  flowId: string
  userId: string
  userName: string
  timestamp: string
}

export interface UserLeftEvent {
  flowId: string
  userId: string
  userName: string
  timestamp: string
}

export interface SyncRequestEvent {
  flowId: string
  requesterId: string
  requesterName: string
}

export interface UseFlowCollaborationOptions {
  flowId: string | null
  enabled?: boolean
  onRemoteChange?: (change: FlowChange) => void
  onUserJoined?: (data: UserJoinedEvent) => void
  onUserLeft?: (data: UserLeftEvent) => void
  onSyncRequest?: (data: SyncRequestEvent) => void
}

export interface UseFlowCollaborationReturn {
  isConnected: boolean
  isConnecting: boolean
  participants: FlowParticipant[]
  error: string | null
  sendNodeAdd: (node: WorkflowNode) => Promise<void>
  sendNodeMove: (nodeId: string, position: { x: number; y: number }) => Promise<void>
  sendNodeUpdate: (nodeId: string, data: Record<string, unknown>) => Promise<void>
  sendNodeDelete: (nodeId: string) => Promise<void>
  sendEdgeAdd: (edge: WorkflowEdge) => Promise<void>
  sendEdgeUpdate: (edgeId: string, data: Record<string, unknown>) => Promise<void>
  sendEdgeDelete: (edgeId: string) => Promise<void>
  sendWorkflowReplace: (nodes: WorkflowNode[], edges: WorkflowEdge[]) => Promise<void>
  refreshParticipants: () => Promise<void>
  requestSync: () => Promise<void>
}

export function useFlowCollaboration({
  flowId,
  enabled = true,
  onRemoteChange,
  onUserJoined,
  onUserLeft,
  onSyncRequest,
}: UseFlowCollaborationOptions): UseFlowCollaborationReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [participants, setParticipants] = useState<FlowParticipant[]>([])
  const [error, setError] = useState<string | null>(null)

  const { isAuthenticated, user } = useAuthStore()
  const onRemoteChangeRef = useRef(onRemoteChange)
  const onUserJoinedRef = useRef(onUserJoined)
  const onUserLeftRef = useRef(onUserLeft)
  const onSyncRequestRef = useRef(onSyncRequest)

  // Keep the callback refs updated
  useEffect(() => {
    onRemoteChangeRef.current = onRemoteChange
    onUserJoinedRef.current = onUserJoined
    onUserLeftRef.current = onUserLeft
    onSyncRequestRef.current = onSyncRequest
  }, [onRemoteChange, onUserJoined, onUserLeft, onSyncRequest])

  // Connect to FlowHub and join flow
  useEffect(() => {
    if (!enabled || !flowId || !isAuthenticated) {
      return
    }

    const token = tokenStorage.getAccessToken()
    if (!token) {
      console.warn('[FlowHub] No auth token available')
      return
    }

    let mounted = true

    const connect = async () => {
      setIsConnecting(true)
      setError(null)

      try {
        // Set up callbacks before connecting
        flowHubService.setCallbacks({
          onUserJoined: (data) => {
            console.log('[FlowHub] User joined:', data.userName)
            // Refresh participants list when someone joins
            flowHubService.getParticipants()
            // Notify consumer
            onUserJoinedRef.current?.(data)
          },
          onUserLeft: (data) => {
            console.log('[FlowHub] User left:', data.userName)
            if (mounted) {
              setParticipants(prev => prev.filter(p => p.userId !== data.userId))
            }
            // Notify consumer
            onUserLeftRef.current?.(data)
          },
          onFlowChange: (change) => {
            console.log('[FlowHub] Received change:', change.changeData.type)
            onRemoteChangeRef.current?.(change)
          },
          onParticipants: (data) => {
            console.log('[FlowHub] Participants updated:', data.participants.length)
            if (mounted) {
              setParticipants(data.participants)
            }
          },
          onError: (message) => {
            console.error('[FlowHub] Error:', message)
            if (mounted) {
              setError(message)
            }
          },
          onReconnecting: () => {
            console.log('[FlowHub] Reconnecting...')
            if (mounted) {
              setIsConnected(false)
            }
          },
          onReconnected: () => {
            console.log('[FlowHub] Reconnected')
            if (mounted) {
              setIsConnected(true)
            }
          },
          onDisconnected: () => {
            console.log('[FlowHub] Disconnected')
            if (mounted) {
              setIsConnected(false)
            }
          },
          onSyncRequest: (data) => {
            console.log('[FlowHub] Sync requested by:', data.requesterName)
            // Only respond if we're the "oldest" participant (first in the list after excluding requester)
            // This prevents multiple users from sending sync data simultaneously
            const currentParticipants = flowHubService.getCachedParticipants()
            const otherParticipants = currentParticipants.filter(p => p.userId !== data.requesterId)

            if (otherParticipants.length > 0 && otherParticipants[0].userId === user?.userId) {
              console.log('[FlowHub] We are the oldest participant, sending sync data')
              onSyncRequestRef.current?.(data)
            } else {
              console.log('[FlowHub] Another participant will handle sync')
            }
          },
        })

        // Connect and join flow
        await flowHubService.connect(token)

        if (!mounted) return

        await flowHubService.joinFlow(flowId)

        if (!mounted) return

        // Get initial participants list
        await flowHubService.getParticipants()

        // Check if there are other participants - if so, request sync
        const currentParticipants = flowHubService.getCachedParticipants()
        const otherParticipants = currentParticipants.filter(p => p.userId !== user?.userId)
        if (otherParticipants.length > 0) {
          console.log('[FlowHub] Other participants present, requesting sync')
          await flowHubService.requestSync()
        }

        if (mounted) {
          setIsConnected(true)
          setIsConnecting(false)
        }
      } catch (err) {
        console.error('[FlowHub] Connection error:', err)
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to connect')
          setIsConnecting(false)
        }
      }
    }

    connect()

    return () => {
      mounted = false
      flowHubService.leaveFlow()
      flowHubService.clearCallbacks()
    }
  }, [flowId, enabled, isAuthenticated, user?.userId])

  // Send node add
  const sendNodeAdd = useCallback(async (node: WorkflowNode) => {
    await flowHubService.sendChange({
      type: 'node_add',
      node: {
        id: node.id,
        type: node.type || 'default',
        position: node.position,
        data: node.data as Record<string, unknown>,
      },
    })
  }, [])

  // Send node move
  const sendNodeMove = useCallback(async (nodeId: string, position: { x: number; y: number }) => {
    await flowHubService.sendChange({
      type: 'node_move',
      nodeId,
      position,
    })
  }, [])

  // Send node update
  const sendNodeUpdate = useCallback(async (nodeId: string, data: Record<string, unknown>) => {
    await flowHubService.sendChange({
      type: 'node_update',
      nodeId,
      data,
    })
  }, [])

  // Send node delete
  const sendNodeDelete = useCallback(async (nodeId: string) => {
    await flowHubService.sendChange({
      type: 'node_delete',
      nodeId,
    })
  }, [])

  // Send edge add
  const sendEdgeAdd = useCallback(async (edge: WorkflowEdge) => {
    await flowHubService.sendChange({
      type: 'edge_add',
      edge: {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || undefined,
        targetHandle: edge.targetHandle || undefined,
        data: edge.data as Record<string, unknown> | undefined,
      },
    })
  }, [])

  // Send edge update
  const sendEdgeUpdate = useCallback(async (edgeId: string, data: Record<string, unknown>) => {
    await flowHubService.sendChange({
      type: 'edge_update',
      edgeId,
      data,
    })
  }, [])

  // Send edge delete
  const sendEdgeDelete = useCallback(async (edgeId: string) => {
    await flowHubService.sendChange({
      type: 'edge_delete',
      edgeId,
    })
  }, [])

  // Refresh participants
  const refreshParticipants = useCallback(async () => {
    await flowHubService.getParticipants()
  }, [])

  // Send complete workflow replacement (for version loading or import)
  const sendWorkflowReplace = useCallback(async (nodes: WorkflowNode[], edges: WorkflowEdge[]) => {
    // Get current change sequence to include with the replacement
    const changeSequence = useWorkflowStore.getState().changeSequence
    await flowHubService.sendChange({
      type: 'workflow_replace',
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.type || 'default',
        position: n.position,
        data: n.data as Record<string, unknown>,
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
        data: e.data as Record<string, unknown> | undefined,
      })),
      changeSequence,
    })
  }, [])

  // Request sync from other participants
  const requestSync = useCallback(async () => {
    await flowHubService.requestSync()
  }, [])

  return {
    isConnected,
    isConnecting,
    participants,
    error,
    sendNodeAdd,
    sendNodeMove,
    sendNodeUpdate,
    sendNodeDelete,
    sendEdgeAdd,
    sendEdgeUpdate,
    sendEdgeDelete,
    sendWorkflowReplace,
    refreshParticipants,
    requestSync,
  }
}

/**
 * Hook to apply remote changes to the workflow store
 * Use this in your workflow editor component
 */
export function useApplyRemoteChanges() {
  const {
    nodes,
    edges,
    addNodeFromData,
    addEdgeFromData,
    updateNode,
    updateNodePosition,
    deleteNode,
    updateEdge,
    deleteEdge,
  } = useWorkflowStore()

  const applyRemoteChange = useCallback((change: FlowChange) => {
    const { changeData } = change

    switch (changeData.type) {
      case 'node_add': {
        // Check if node already exists (avoid duplicates)
        const exists = nodes.some(n => n.id === changeData.node.id)
        if (!exists) {
          addNodeFromData(changeData.node as WorkflowNode)
        }
        break
      }

      case 'node_move': {
        updateNodePosition(changeData.nodeId, changeData.position)
        break
      }

      case 'node_update': {
        updateNode(changeData.nodeId, changeData.data)
        break
      }

      case 'node_delete': {
        deleteNode(changeData.nodeId)
        break
      }

      case 'edge_add': {
        // Check if edge already exists
        const exists = edges.some(e => e.id === changeData.edge.id)
        if (!exists) {
          addEdgeFromData(changeData.edge as WorkflowEdge)
        }
        break
      }

      case 'edge_update': {
        updateEdge(changeData.edgeId, changeData.data as { label?: string; conditionBranchKey?: string })
        break
      }

      case 'edge_delete': {
        deleteEdge(changeData.edgeId)
        break
      }

      case 'bulk_change': {
        // Apply each change in the bulk
        changeData.changes.forEach(subChange => {
          applyRemoteChange({ ...change, changeData: subChange })
        })
        break
      }

      case 'workflow_replace': {
        // Replace entire workflow - used when another user loads a version or imports
        const store = useWorkflowStore.getState()
        const currentSeq = store.changeSequence
        const incomingSeq = changeData.changeSequence

        // Only apply if incoming change sequence is newer (or not specified - backwards compatibility)
        if (incomingSeq === undefined || incomingSeq > currentSeq) {
          console.log(`[FlowHub] Applying workflow_replace (seq: ${incomingSeq} > ${currentSeq})`)
          store.loadFromRemoteReplace(changeData.nodes, changeData.edges, incomingSeq)
        } else {
          console.log(`[FlowHub] Ignoring stale workflow_replace (seq: ${incomingSeq} <= ${currentSeq})`)
        }
        break
      }
    }
  }, [nodes, edges, addNodeFromData, addEdgeFromData, updateNode, updateNodePosition, deleteNode, updateEdge, deleteEdge])

  return { applyRemoteChange }
}
